// 贝叶斯AI性格测试系统 - 主应用逻辑
import stateManager from './state-manager.js';
import api from './api.js';
import { APP_CONFIG, DEFAULT_ANALYSIS_DATA, UI_TEXTS } from './config.js';

class PersonalityTestApp {
    constructor() {
        this.stateManager = stateManager;
        this.api = api;
        this.init();
    }

    // 初始化应用
    init() {
        this.initUI();
        this.bindEvents();
        this.setupStateListener();
        this.initVersionInfo();
        this.showWelcomeMessage();
    }

    // 初始化UI文案
    initUI() {
        // 设置页面标题和meta信息
        document.title = UI_TEXTS.TITLE;
        document.querySelector('meta[name="keywords"]')?.setAttribute('content', UI_TEXTS.KEYWORDS);
        document.querySelector('meta[name="description"]')?.setAttribute('content', UI_TEXTS.DESCRIPTION);

        // 设置页面文案
        this.setText('#home-page .logo', UI_TEXTS.HOME_LOGO);
        this.setText('#home-page .subtitle', UI_TEXTS.HOME_SUBTITLE);
        this.setText('#start-btn', `<i class="fas fa-play-circle"></i> ${UI_TEXTS.START_BUTTON}`);
        
        // 设置首页footer
        const homeFooter = document.querySelector('#home-page .footer');
        if (homeFooter) {
            const versionInfo = homeFooter.querySelector('.version-info');
            homeFooter.innerHTML = `${UI_TEXTS.COPYRIGHT}${versionInfo ? versionInfo.outerHTML : ''}`;
        }

        // 采集页面
        this.setText('#capture-page .logo', UI_TEXTS.CAPTURE_TITLE);
        this.setText('#capture-page .subtitle', UI_TEXTS.CAPTURE_SUBTITLE);
        this.setText('#capture-btn', `<i class="fas fa-camera"></i> ${UI_TEXTS.RETAKE_BUTTON}`);
        this.setText('#startai-btn', `<i class="fas fa-play"></i> ${UI_TEXTS.START_ANALYSIS_BUTTON}`);
        
        const instructions = document.querySelectorAll('#capture-page .instructions li');
        if (instructions[0]) instructions[0].textContent = UI_TEXTS.INSTRUCTION_1;
        if (instructions[1]) instructions[1].textContent = UI_TEXTS.INSTRUCTION_2;

        // 分析页面
        this.setText('.loading-text', UI_TEXTS.LOADING_TITLE);
        this.setText('.loading-subtext', UI_TEXTS.LOADING_SUBTITLE);
        this.setText('#loading-page .footer', UI_TEXTS.LOADING_FOOTER);
    }

    // 设置文本内容的辅助方法
    setText(selector, content) {
        const element = document.querySelector(selector);
        if (element) {
            element.innerHTML = content;
        }
    }

    // 绑定事件
    bindEvents() {
        const events = [
            ['start-btn', 'click', () => this.goToCapture()],
            ['capture-btn', 'click', () => this.retakePhoto()],
            ['startai-btn', 'click', () => this.startAnalysis()],
            ['reanalyze-btn', 'click', () => this.goToCapture()],
            ['countdown-timer-preview', 'click', () => this.goToHome()],
            ['countdown-timer-result', 'click', () => this.goToHome()],
            ['error-retry-btn', 'click', () => this.handleErrorRetry()],
            ['error-home-btn', 'click', () => this.handleErrorHome()]
        ];

        events.forEach(([id, event, handler]) => {
            const element = document.getElementById(id);
            if (element) {
                element.addEventListener(event, handler);
            }
        });

        // 点击弹窗背景关闭弹窗
        const errorModal = document.getElementById('error-modal');
        if (errorModal) {
            errorModal.addEventListener('click', (e) => {
                if (e.target === errorModal) {
                    this.hideErrorModal();
                }
            });
        }
    }

    // 设置状态监听器
    setupStateListener() {
        this.stateManager.addListener((oldState, newState) => {
            if (oldState.currentPage !== newState.currentPage) {
                this.handlePageChange(newState.currentPage);
            }
        });
    }

    // 页面切换处理
    handlePageChange(newPage) {
        document.querySelectorAll('.page').forEach(page => page.classList.remove('active'));
        document.getElementById(`${newPage}-page`).classList.add('active');
        this.stateManager.clearTimers();
    }

    // 导航方法
    goToHome() {
        this.stateManager.resetAll();
        this.resetCaptureUI();
        this.stateManager.setState({ currentPage: APP_CONFIG.PAGES.HOME });
        this.speak(APP_CONFIG.MESSAGES.AUTO_RETURN);
    }

    async goToCapture() {
        this.stateManager.resetCaptureState();
        this.resetCaptureUI();
        this.stateManager.setState({ currentPage: APP_CONFIG.PAGES.CAPTURE });
        await this.initCamera();
        this.checkPerson();
    }

    async retakePhoto() {
        this.stateManager.clearTimers();
        this.resetCaptureUI();
        await this.initCamera();
        await this.speak(APP_CONFIG.MESSAGES.RETAKE);
        setTimeout(() => this.startPhotoCountdown(), 6000);
    }

    async startAnalysis() {
        this.stateManager.clearTimers();
        this.stateManager.setState({ currentPage: APP_CONFIG.PAGES.LOADING });
        this.stateManager.stopCamera();
        this.startProgressAnimation();
        await this.speak(APP_CONFIG.MESSAGES.ANALYZING);
        //将拍摄获得的图片上传到oss
        const imageData = this.stateManager.getState().capturedImage;
        const blob = await fetch(imageData).then(res => res.blob());
        const filename = `${Date.now()}.jpg`;
        const url = await this.api.uploadImageToOSS(blob, filename);
        //
        // return this.showAnalysisResult(DEFAULT_ANALYSIS_DATA);
        //调用llm_face_job接口
        // let res = await this.api.myFetch('/api/llm_face_job', { image: url });
        // if(res.code !== 0){
        //     await this.speak(APP_CONFIG.MESSAGES.ANALYSIS_FAILED);
        //     return 
        // }
        // 调用接口分析舌象
        const res = await this.api.myFetch('/api/tongue_analysis_advance', { image: url });
        // if(res.code !== 0){
        //     await this.speak(APP_CONFIG.MESSAGES.ANALYSIS_FAILED);
        //     return 
        // }
        console.log('API返回结果:', res);
        this.showAnalysisResult(res);
        // setTimeout(() => {
        //     this.showAnalysisResult(DEFAULT_ANALYSIS_DATA);
        // }, 5000);
    }

    // UI状态管理
    resetCaptureUI() {
        const elements = {
            camera: document.getElementById('camera-container'),
            avatar: document.getElementById('avatar-container'),
            actions: document.getElementById('action-buttons'),
            countdown: document.getElementById('countdown-overlay'),
            captureBtn: document.getElementById('capture-btn')
        };

        elements.camera?.classList.remove('hide');
        elements.avatar?.classList.add('hide');
        elements.actions?.classList.add('hide');
        if (elements.countdown) elements.countdown.style.display = 'none';
        if (elements.captureBtn) {
            elements.captureBtn.disabled = false;
            elements.captureBtn.innerHTML = '<i class="fas fa-camera"></i> 重新拍摄';
        }
    }

    // 摄像头管理
    async initCamera() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
            const video = document.getElementById('video');
            video.srcObject = stream;
            this.stateManager.setState({ videoStream: stream });
            await video.play();
        } catch (error) {
            alert(APP_CONFIG.ERRORS.CAMERA_ACCESS);
        }
    }

    // 人脸检测
    async checkPerson() {
        const state = this.stateManager.getState();
        if (state.isRecognizing) return;
        
        try {
            const video = document.getElementById('video');
            const url = await this.api.captureAndUpload(video);
            const res = await this.api.myFetch('/api/face_detect_plus', { image: url });
            await this.handleFaceDetectionResult(res);
        } catch (error) {
            setTimeout(() => this.checkPerson(), APP_CONFIG.COUNTDOWN.FACE_CHECK_INTERVAL);
        }
    }

    async handleFaceDetectionResult(res) {
        if (res.code !== 0) {
            setTimeout(() => {
                const state = this.stateManager.getState();
                if (!state.isRecognizing) this.checkPerson();
            }, APP_CONFIG.COUNTDOWN.FACE_CHECK_INTERVAL);
            return;
        }

        if (res.data) {
            console.log('res.data', res.data);
            await this.speak(APP_CONFIG.MESSAGES.PREPARE_CAPTURE);
            console.log('开始countdown')
            this.startPhotoCountdown();
        } else {
            const state = this.stateManager.getState();
            if (!state.isRecognizing) {
                setTimeout(() => this.checkPerson(), APP_CONFIG.COUNTDOWN.FACE_CHECK_INTERVAL);
            }
        }
    }

    // 拍摄倒计时
    startPhotoCountdown() {
        const overlay = document.getElementById('countdown-overlay');
        const number = document.getElementById('countdown-number');
        const captureBtn = document.getElementById('capture-btn');
        
        captureBtn.disabled = true;
        captureBtn.innerHTML = '<i class="fas fa-circle-notch fa-spin"></i> 重新拍摄';
        overlay.style.display = 'flex';
        
        let count = APP_CONFIG.COUNTDOWN.PHOTO_COUNTDOWN;
        number.textContent = count;
        
        // 播报初始数字（5）
        this.speak(count);
        
        const interval = setInterval(() => {
            count--;
            number.textContent = count;
            number.style.animation = 'none';
            setTimeout(() => number.style.animation = 'pulse 1s', 10);
            
            if (count > 0) {
                this.speak(count);
            } else {
                clearInterval(interval);
                overlay.style.display = 'none';
                captureBtn.disabled = false;
                captureBtn.innerHTML = '<i class="fas fa-camera"></i> 重新拍摄';
                this.speak(APP_CONFIG.MESSAGES.CAPTURE);
                this.capturePhoto();
            }
        }, APP_CONFIG.ANIMATION.COUNTDOWN_INTERVAL);
    }

    // 拍摄照片
    capturePhoto() {
        const video = document.getElementById('video');
        if (!video || video.videoWidth === 0) return;

        const canvas = document.createElement('canvas');
        
        // 设置canvas尺寸，与scan-circle的显示尺寸一致
        // 桌面端716px，移动端300px
        const scanCircle = document.querySelector('.scan-circle');
        const scanStyle = window.getComputedStyle(scanCircle);
        const size = parseInt(scanStyle.width);
        canvas.width = size;
        canvas.height = size;
        
        const ctx = canvas.getContext('2d');
        
        // 获取camera-container和scan-circle的尺寸
        const cameraContainer = document.getElementById('camera-container');
        const cameraStyle = window.getComputedStyle(cameraContainer);
        const containerSize = parseInt(cameraStyle.width);
        
        // 计算scan-circle在video中的对应区域
        // video显示尺寸与container相同，但video元素的CSS可能放大了
        const videoDisplayWidth = parseInt(window.getComputedStyle(video).width);
        const videoDisplayHeight = parseInt(window.getComputedStyle(video).height);
        
        // 计算缩放比例
        const scaleX = video.videoWidth / videoDisplayWidth;
        const scaleY = video.videoHeight / videoDisplayHeight;
        
        // scan-circle在容器中是居中的，计算其在video中的对应位置
        const cropSize = size * Math.min(scaleX, scaleY);
        const cropX = (video.videoWidth - cropSize) / 2;
        const cropY = (video.videoHeight - cropSize) / 2;
        
        // 从video的中心区域截取与scan-circle大小对应的部分
        ctx.drawImage(video, cropX, cropY, cropSize, cropSize, 0, 0, size, size);
        
        const imageData = canvas.toDataURL(APP_CONFIG.IMAGE.PREVIEW_FORMAT);
        this.stateManager.setState({ capturedImage: imageData });
        
        // 切换到预览状态
        document.getElementById('camera-container').classList.add('hide');
        document.getElementById('avatar-container').classList.remove('hide');
        document.getElementById('action-buttons').classList.remove('hide');
        document.getElementById('captured-img-preview').src = imageData;
        document.getElementById('captured-img').src = imageData;
        
        this.startAutoReturn('countdown-time-preview');
    }


    // 舌象数据处理函数
    async showAnalysisResult(res) {
        console.log('3秒loading完成，准备显示结果:', res);
        
        // 检查API返回结果
        if (!res) {
            console.error('API返回结果为空');
            await this.speak('分析失败：服务器无响应，请重试');
            this.showErrorModal('分析失败：服务器无响应，请重试');
            return;
        }
        
        if (res.code !== 0) {
            console.error('API返回错误:', res.message || '未知错误');
            const errorMessage = `分析失败：${res.message || '服务器处理异常，请重试'}`;
            await this.speak(errorMessage);
            this.showErrorModal(errorMessage);
            return;
        }
        
        if (!res.data) {
            console.error('API返回数据为空');
            await this.speak('分析失败：未获取到分析数据，请重试');
            this.showErrorModal('分析失败：未获取到分析数据，请重试');
            return;
        }
        
        // 将分析结果存储到sessionStorage，以便在report.html中使用
        sessionStorage.setItem('tongueAnalysisResult', JSON.stringify(res.data));
        console.log('分析数据已保存到sessionStorage');
        
        // 跳转到report.html
        window.location.href = './report.html';
    }

    displayAnalysisData(data) {
        const elements = {
            gender: document.getElementById('gender'),
            age: document.getElementById('age'),
            bmi: document.getElementById('bmi'),
            health: document.getElementById('health'),
            traitList: document.querySelector('.trait-list'),
            careerList: document.querySelector('.career-list')
        };

        if (elements.gender) elements.gender.textContent = data.gender;
        if (elements.age) elements.age.textContent = data.age + '岁';
        if (elements.bmi) elements.bmi.textContent = data.bmi;
        if (elements.health) elements.health.textContent = data.health + '/100';
        
        if (elements.traitList) {
            elements.traitList.innerHTML = data.features.map(feature => `
                <div class="trait-item">
                    <div class="trait-name">${feature.title}</div>
                    <div class="trait-desc">${feature.content}</div>
                </div>
            `).join('');
        }
        
        if (elements.careerList) {
            elements.careerList.innerHTML = data.careerSuggestions.map(career => `
                <div class="career-item">
                    <div class="career-text">
                        <h4>${career.title}</h4>
                        <p>${career.content}</p>
                    </div>
                </div>
            `).join('');
        }
    }

    // 进度条动画
    startProgressAnimation() {
        const progressBar = document.querySelector('.progress-bar');
        if (!progressBar) return;
        
        progressBar.style.width = '0%';
        let progress = 0;
        
        const interval = setInterval(() => {
            progress += Math.random() * APP_CONFIG.ANIMATION.PROGRESS_INCREMENT;
            if (progress > APP_CONFIG.ANIMATION.PROGRESS_MAX) progress = APP_CONFIG.ANIMATION.PROGRESS_MAX;
            progressBar.style.width = progress + '%';
        }, APP_CONFIG.ANIMATION.PROGRESS_UPDATE_INTERVAL);
        
        this.stateManager.setState({ progressInterval: interval });
    }

    stopProgressAnimation() {
        const state = this.stateManager.getState();
        if (state.progressInterval) {
            clearInterval(state.progressInterval);
            this.stateManager.setState({ progressInterval: null });
        }
        const progressBar = document.querySelector('.progress-bar');
        if (progressBar) progressBar.style.width = '100%';
    }

    // 自动返回倒计时
    startAutoReturn(timerId) {
        this.stateManager.clearTimers();
        let time = APP_CONFIG.COUNTDOWN.AUTO_RETURN_TIME;
        const element = document.getElementById(timerId);
        if (!element) return;
        
        element.textContent = time;
        const interval = setInterval(() => {
            time--;
            element.textContent = time;
            if (time <= 0) {
                this.goToHome();
            }
        }, 1000);
        
        this.stateManager.setState({ countdownInterval: interval });
    }

    // 语音播报
    async speak(text) {
        return new Promise((resolve) => {
            if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
                const utterance = new SpeechSynthesisUtterance(text.toString());
                utterance.lang = APP_CONFIG.SPEECH.LANG;
                utterance.rate = APP_CONFIG.SPEECH.RATE;
                utterance.pitch = APP_CONFIG.SPEECH.PITCH;
                
                // 当语音播放结束时resolve
                utterance.onend = () => {
                    resolve();
                };
                
                // 处理语音播放错误
                utterance.onerror = () => {
                    resolve(); // 即使出错也resolve，避免阻塞流程
                };
                
                window.speechSynthesis.speak(utterance);
            } else {
                // 如果不支持语音合成，立即resolve
                resolve();
            }
        });
    }

    // 初始化版本信息
    initVersionInfo() {
        const versionNumber = document.getElementById('version-number');
        const buildDate = document.getElementById('build-date');
        
        if (versionNumber) {
            versionNumber.textContent = `v${APP_CONFIG.VERSION.NUMBER}`;
        }
        
        if (buildDate) {
            buildDate.textContent = APP_CONFIG.VERSION.BUILD_DATE;
        }
        
        // 在控制台输出版本信息
        console.log(`%c${APP_CONFIG.VERSION.NAME}`, 'color: #4facfe; font-size: 14px; font-weight: bold');
        console.log(`%c版本: ${APP_CONFIG.VERSION.NUMBER}`, 'color: #00f2fe; font-size: 12px');
        console.log(`%c构建日期: ${APP_CONFIG.VERSION.BUILD_DATE}`, 'color: #00f2fe; font-size: 12px');
    }

    // 欢迎语音
    async showWelcomeMessage() {
        setTimeout(async () => {
            await this.speak(APP_CONFIG.MESSAGES.WELCOME);
        }, 1000);
    }

    // 弹窗相关方法
    showErrorModal(message) {
        const modal = document.getElementById('error-modal');
        const messageElement = document.getElementById('error-message');
        
        if (modal && messageElement) {
            messageElement.textContent = message;
            modal.classList.add('show');
            
            // 停止进度条动画
            this.stopProgressAnimation();
        }
    }

    hideErrorModal() {
        const modal = document.getElementById('error-modal');
        if (modal) {
            modal.classList.remove('show');
        }
    }

    handleErrorRetry() {
        this.hideErrorModal();
        // 回到拍摄页面重新拍摄
        this.goToCapture();
    }

    handleErrorHome() {
        this.hideErrorModal();
        // 返回首页
        this.goToHome();
    }
}

// 初始化应用
document.addEventListener('DOMContentLoaded', () => {
    new PersonalityTestApp();
}); 